import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {MOLBIO_ANALYTICS_ACTIONS, MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import type {PredictionBatchSource} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import staticURL from 'lib/staticURL';
import {AgentTypeError} from 'llm/chat-library/ChatDraftState';
import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import {getScientificAIDisabledMessage} from 'model-hub/permissions/ModelHubPermissions';
import NoModelsInstalledEmptyState from 'model-hub/scientific-models/NoModelsInstalledEmptyState';
import {ScientificModelPreferences} from 'model-hub/scientific-models/ScientificModelConstants';
import {
  getMaxAminoAcidCount,
  resolveInitialStructurePredictionModel,
  type ScientificModelNode,
  supportsMSA,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {RemainingExecutionsAndTenantNameQuery} from 'model-hub/scientific-models/structure-prediction/__generated/RemainingExecutionsAndTenantNameQuery';
import {UserVerificationStatusQuery} from 'model-hub/scientific-models/structure-prediction/__generated/UserVerificationStatusQuery';
import {
  type AaSequenceInput,
  ACADEMIC_VERIFICATION_MESSAGE,
  type InputComponent,
  isAcademicUserBlocked,
  isProteinInput,
  type ProteinInput,
  REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY,
  requestRun,
  USER_VERIFICATION_STATUS,
} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import StickyFooter, {
  STICKY_FOOTER_CONTAINER_STYLES,
  STICKY_FOOTER_CONTENT_STYLES,
} from 'model-hub/StickyFooter';
import {navigateToProteinStructureTab} from 'molbio/aa-sequences/alphafold/AlphaFoldTabUtils';
import notifications from 'notifications';
import {FONT_PROXIMA_13, FONT_PROXIMA_17} from 'styles/FontConstants';
import InlineBanner, {InlineBannerActionType, InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE, GRAY_DARKER} from 'ui-library/Colors';
import usePreference from 'ui-library/hooks/usePreference';
import {CenteredSpinner} from 'ui-library/Spinner';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';

import ComponentSelector from './ComponentSelector';
import {
  ComponentValidationResult,
  flattenComponentSelectionToComponentIds,
  getComponentValidationResult,
  getValidationErrorMessages,
  SelectedComponents,
} from './ComponentUtils';
import {hasNucleotideOnlySequences, openDNASequenceWarningModal} from './DNASequenceWarningModal';
import ModelSelector from './ModelSelector';

function makePrimaryComponentSelection(inputComponent: InputComponent): SelectedComponents {
  return {
    [inputComponent.id]: {
      aminoAcids: getInputAminoAcids(inputComponent),
      count: 1,
      moleculeCount: isProteinInput(inputComponent) ? inputComponent.chains.length : 1,
      numHeavyAtoms: 0,
      entityType: isProteinInput(inputComponent)
        ? ModelTypeConstants.PROTEIN
        : ModelTypeConstants.AA_SEQUENCE,
      permissions: {
        readable: inputComponent.permissions.readable ?? false,
        structurePredictionEditable: inputComponent.permissions.structurePredictionEditable ?? false,
      },
      ...(inputComponent.folderId != null && {folderId: inputComponent.folderId}),
    },
  };
}

const submitStructurePrediction = async (
  inputComponentId: string | null,
  folderId: string | null,
  selectedComponents: SelectedComponents,
  selectedModel: ScientificModelNode,
  msaEnabled: boolean,
  setSubmitting: (submitting: boolean) => void,
  remainingExecutionsAndTenantNameData: RemainingExecutionsAndTenantNameQuery,
  onSubmit?: () => void,
  source?: PredictionBatchSource,
  primaryComponentVersionId?: string
): Promise<AgentTypeError | null> => {
  setSubmitting(true);
  try {
    const {proteinIds, moleculeIds, aaSequenceIds} =
      flattenComponentSelectionToComponentIds(selectedComponents);
    const primaryComponentId = inputComponentId ?? proteinIds[0] ?? aaSequenceIds[0];
    if (primaryComponentId == null) {
      throw new Error('No primary component id found');
    }
    const submissionFolderId = folderId ?? selectedComponents[primaryComponentId]?.folderId;
    if (!submissionFolderId) {
      throw new Error('No folder id found');
    }
    const primaryAaSequenceId = APIUtil.isBasicFolderItem(primaryComponentId)
      ? undefined
      : primaryComponentId;
    await requestRun({
      aaSequenceIds,
      moleculeIds,
      proteinIds,
      primaryComponentId,
      primaryComponentVersionId,
      folderId: submissionFolderId,
      remainingExecutionsAndTenantNameData,
      selectedModel,
      msaEnabled,
      source,
    });
    trackEvent(MOLBIO_ANALYTICS_ACTIONS.REQUESTED_ALPHAFOLD_RUN);
    if (inputComponentId == null) {
      await navigateToProteinStructureTab(primaryAaSequenceId ?? aaSequenceIds[0]);
    }
    onSubmit?.();
    return null;
  } catch (error) {
    const creditsError = getInsufficientCreditsAgentTypeError(error);
    if (creditsError) {
      return creditsError;
    }
    if (net.isServerRequestError(error)) {
      notifications.notifyError(error);
      return null;
    } else {
      notifications.error('Error when submitting structure prediction. Please try again.');
    }
    throw error;
  } finally {
    setSubmitting(false);
  }
};

export interface StructurePredictionFormProps {
  availableModels: ReadonlyArray<ScientificModelNode>;
  inputComponent?: ProteinInput | AaSequenceInput;
  startingModel?: ScientificModelNode;
  onRunAllModels?: () => Promise<boolean>;
  onSubmit?: () => void;
  onBack?: () => void;
  /** Identifies which UI surface triggered this form (protein tab vs model hub). */
  source?: PredictionBatchSource;
  /** Version id of the primary protein component, used to evict that version's cached prediction jobs. */
  primaryComponentVersionId?: string;
}

function getInputAminoAcids(inputComponent: InputComponent): string {
  if (isProteinInput(inputComponent)) {
    return inputComponent.chains.join('');
  }
  return inputComponent.aminoAcids;
}

export default function StructurePredictionForm({
  availableModels,
  ...props
}: StructurePredictionFormProps): JSX.Element {
  if (availableModels.length === 0) {
    return <NoModelsInstalledEmptyState />;
  }
  return <LoadedStructurePredictionForm availableModels={availableModels} {...props} />;
}

function LoadedStructurePredictionForm({
  availableModels,
  inputComponent,
  startingModel,
  onRunAllModels,
  onSubmit,
  onBack,
  source,
  primaryComponentVersionId,
}: StructurePredictionFormProps): JSX.Element {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [creditError, setCreditError] = useState<AgentTypeError | null>(null);
  const submitInFlightRef = useRef<boolean>(false);
  // If an input model is provided, use it. Otherwise, use the default structure prediction model
  // as the start and allow the user to change the model.
  const showModelSelector =
    startingModel == null && BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS;
  const [lastSelectedModel, setLastSelectedModel] = usePreference<string | undefined>(
    ScientificModelPreferences.LAST_SELECTED_STRUCTURE_PREDICTION_MODEL
  );
  const [selectedModel, setSelectedModel] = useState<ScientificModelNode>(() =>
    resolveInitialStructurePredictionModel(
      availableModels,
      startingModel?.externalReference,
      lastSelectedModel
    )
  );
  const [msaEnabled, setMSAEnabled] = useState<boolean>(() => supportsMSA(selectedModel));

  const needsComponentSelection = inputComponent == null;
  const remainingExecutionsAndTenantNameQueryResult =
    useBenchlingQuery<RemainingExecutionsAndTenantNameQuery>(REMAINING_EXECUTIONS_AND_TENANT_NAME_QUERY);
  const userVerificationStatusQueryResult =
    useBenchlingQuery<UserVerificationStatusQuery>(USER_VERIFICATION_STATUS);

  const [selectedComponents, setSelectedComponents] = useState<SelectedComponents>(
    !needsComponentSelection ? makePrimaryComponentSelection(inputComponent) : {}
  );

  const [validationResult, setValidationResult] = useState<ComponentValidationResult | null>(null);
  useEffect(() => {
    const result = getComponentValidationResult(selectedComponents, selectedModel);
    setValidationResult(result);
  }, [selectedComponents, selectedModel]);

  // Sync selectedComponents when inputComponentId or store data change: clear when no primary,
  // reset to new primary when it changes, or update amino acids/permissions when they change in the store
  useEffect(() => {
    setSelectedComponents((prevComponents) => {
      if (inputComponent == null) {
        return {};
      }
      const primary = prevComponents[inputComponent.id];
      if (primary) {
        const nextAminoAcids = getInputAminoAcids(inputComponent);
        const nextMoleculeCount = isProteinInput(inputComponent) ? inputComponent.chains.length : 1;
        const nextPermissions = {
          readable: inputComponent.permissions.readable ?? false,
          structurePredictionEditable: inputComponent.permissions.structurePredictionEditable ?? false,
        };
        const aminoAcidsChanged = primary.aminoAcids !== nextAminoAcids;
        const moleculeCountChanged = primary.moleculeCount !== nextMoleculeCount;
        const permissionsChanged =
          primary.permissions.readable !== nextPermissions.readable ||
          primary.permissions.structurePredictionEditable !== nextPermissions.structurePredictionEditable;
        const folderIdChanged = primary.folderId !== inputComponent.folderId;
        if (aminoAcidsChanged || moleculeCountChanged || permissionsChanged || folderIdChanged) {
          return {
            ...prevComponents,
            [inputComponent.id]: {
              ...primary,
              ...(aminoAcidsChanged && {aminoAcids: nextAminoAcids}),
              ...(moleculeCountChanged && {moleculeCount: nextMoleculeCount}),
              ...(permissionsChanged && {permissions: nextPermissions}),
              ...(folderIdChanged && {folderId: inputComponent.folderId}),
            },
          };
        }
        return prevComponents;
      }
      return makePrimaryComponentSelection(inputComponent);
    });
    // If the caller passes a freshly-allocated inputComponent object each render this effect re-runs,
    // but the updater is idempotent: it returns the previous state when nothing meaningful changed, so
    // React bails out and any separately-added components are preserved.
  }, [inputComponent]);

  if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.PENDING ||
    userVerificationStatusQueryResult.type === QueryResultType.PENDING
  ) {
    return <CenteredSpinner />;
  } else if (
    remainingExecutionsAndTenantNameQueryResult.type === QueryResultType.ERROR ||
    userVerificationStatusQueryResult.type === QueryResultType.ERROR
  ) {
    notifications.error('Error when loading structure prediction form. Please refresh the page.');
    return <></>;
  }

  const currentUser = userVerificationStatusQueryResult.data.currentUser;
  assert(
    currentUser?.__typename === 'UserNode' || currentUser == null,
    `Non-user should not access this page.`
  );

  let blockingBannerMessage: string | null = getScientificAIDisabledMessage();
  if (blockingBannerMessage == null) {
    if (isAcademicUserBlocked(currentUser)) {
      blockingBannerMessage = ACADEMIC_VERIFICATION_MESSAGE;
    } else if (remainingExecutionsAndTenantNameQueryResult.data.remainingAlphafoldExecutions === 0) {
      blockingBannerMessage = 'You do not have any remaining structure prediction runs.';
    } else if (validationResult != null) {
      // Show validation banner for any selection size (0, 1, or 2+ components); wording depends on isSingleComponent
      const isSingleComponent =
        inputComponent != null &&
        Object.keys(selectedComponents).length === 1 &&
        selectedComponents[inputComponent.id]?.count === 1;
      blockingBannerMessage = getValidationErrorMessages(validationResult, isSingleComponent).join('\n');
    }
  }

  const handleModelChange = (model: ScientificModelNode): void => {
    setSelectedModel(model);
    setMSAEnabled(supportsMSA(model));
    setLastSelectedModel(model.externalReference);
    if (inputComponent != null) {
      setSelectedComponents(makePrimaryComponentSelection(inputComponent));
    } else {
      setSelectedComponents({});
    }
  };

  const handleSubmitClick = async (): Promise<void> => {
    if (submitInFlightRef.current) {
      return;
    }
    setCreditError(null);
    if (hasNucleotideOnlySequences(selectedComponents)) {
      const shouldProceedWithSubmission = await openDNASequenceWarningModal();
      if (!shouldProceedWithSubmission || submitInFlightRef.current) {
        return;
      }
    }
    await performSubmit();
  };

  const performSubmit = async (): Promise<void> => {
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;
    setCreditError(null);
    try {
      const error = await submitStructurePrediction(
        inputComponent?.id ?? null,
        inputComponent?.folderId ?? null,
        selectedComponents,
        selectedModel,
        msaEnabled,
        setSubmitting,
        remainingExecutionsAndTenantNameQueryResult.data,
        onSubmit,
        source,
        primaryComponentVersionId
      );
      if (error) {
        setCreditError(error);
      }
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return (
    <div
      className={css(styles.outerContainer)}
      {...getTestProps({component: 'StructurePredictionForm', element: 'root'})}
    >
      <div className={css(styles.content)}>
        {creditError != null && (
          <div className={css(styles.largeInlineBannerMargin)}>
            <InlineBanner
              bannerType={InlineBannerType.WARNING}
              text={creditError.title}
              supportingInfo={creditError.detail}
            />
          </div>
        )}
        {showModelSelector && onRunAllModels && !blockingBannerMessage && !creditError && (
          <div className={css(styles.largeInlineBannerMargin)}>
            <InlineBanner
              bannerType={InlineBannerType.INFO}
              hideIcon={true}
              text={`Not sure which model to run? Compare them by running all ${availableModels.length}.`}
              action={{
                actionType: InlineBannerActionType.CUSTOM,
                actionText: submitting ? 'Submitting...' : 'Run All',
                actionOnClick: async () => {
                  if (submitting) {
                    return;
                  }
                  trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.CLICKED_STRUCTURE_PREDICTION_RUN_ALL_MODELS, {
                    actionOrigin: ActionOrigin.MODEL_HUB_STRUCTURE_PREDICTION_FORM_PAGE,
                  });
                  setCreditError(null);
                  setSubmitting(true);
                  let didQueueRuns = false;
                  try {
                    didQueueRuns = (await onRunAllModels?.()) ?? false;
                  } catch (error) {
                    const creditsError = getInsufficientCreditsAgentTypeError(error);
                    if (creditsError) {
                      setCreditError(creditsError);
                      return;
                    }
                    throw error;
                  } finally {
                    setSubmitting(false);
                    if (didQueueRuns) {
                      onSubmit?.();
                    }
                  }
                },
                buttonStyle: submitting ? ButtonStyle.TEXT : ButtonStyle.PRIMARY,
                rightAlign: true,
              }}
            />
          </div>
        )}

        <div className={css(styles.contentPadding)}>
          {blockingBannerMessage != null && (
            <div className={css(styles.largeInlineBannerMargin)}>
              <InlineBanner text={blockingBannerMessage} bannerType={InlineBannerType.WARNING} />
            </div>
          )}
          {blockingBannerMessage == null && <div className={css(styles.pseudoBanner)} />}
          <div className={css(styles.centered, styles.imgMargin)}>
            <img
              width={255}
              height={175}
              src={staticURL('/static/img/alphafold_example_quality_structure.png')}
            />
          </div>
          <p className={css(styles.largestText, styles.centered)}>Predict 3D structure</p>
          <p className={css(styles.smallerText, styles.grayDarker, styles.centered)}>
            Predict structures of proteins up to {getMaxAminoAcidCount(selectedModel)} residues using&nbsp;
            {selectedModel.displayName}
          </p>
        </div>

        {showModelSelector && (
          <div className={css(styles.centered, styles.modelSelectorContainer)}>
            <ModelSelector
              selectedModel={selectedModel}
              availableModels={availableModels}
              msaEnabled={msaEnabled}
              onModelChange={handleModelChange}
              onMSAEnabledChange={setMSAEnabled}
              disabled={false}
              className={css(styles.modelSelector)}
            />
          </div>
        )}

        <div className={css(styles.centered)}>
          <a
            href={selectedModel.paperLink}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.modelLink)}
          >
            Learn more about {selectedModel.displayName}
          </a>
        </div>

        {(selectedModel.capabilities.supportsMultimers || needsComponentSelection) && (
          <ComponentSelector
            selectedComponents={selectedComponents}
            onComponentsChange={setSelectedComponents}
            primaryComponentId={inputComponent?.id ?? null}
            model={selectedModel}
          />
        )}
      </div>
      <StickyFooter>
        {onBack && (
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            text="Cancel"
            onClick={onBack}
          />
        )}
        <Button
          buttonStyle={ButtonStyle.SECONDARY}
          size={ButtonSize.MEDIUM}
          text={submitting ? 'Submitting...' : 'Predict 3D structure'}
          onClick={handleSubmitClick}
          disabled={validationResult != null || submitting || blockingBannerMessage != null}
          {...getTestProps({component: 'StructurePredictionForm', element: 'submitButton'})}
        />
      </StickyFooter>
    </div>
  );
}

const styles = StyleSheet.create({
  outerContainer: STICKY_FOOTER_CONTAINER_STYLES,
  content: STICKY_FOOTER_CONTENT_STYLES,
  contentPadding: {
    padding: '20px',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  title: {
    ...FONT_PROXIMA_17,
    fontWeight: 600,
    color: GRAY_DARKER,
  },
  description: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginTop: '8px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
  },
  imgMargin: {
    margin: '34px 0px',
  },
  largestText: FONT_PROXIMA_17,
  smallerText: FONT_PROXIMA_13,
  grayDarker: {
    color: GRAY_DARKER,
  },
  modelSelectorContainer: {
    margin: '20px 0px',
  },
  modelSelector: {
    // Definite width so the selector doesn't shrink to fit the help text, which varies by model.
    width: '100%',
    minWidth: '400px',
    maxWidth: '632px',
    boxSizing: 'border-box',
    textAlign: 'left',
    padding: '0px 16px',
  },
  largeInlineBannerMargin: {
    margin: '18px 11px',
  },
  pseudoBanner: {
    height: '35.5px',
    margin: '18px 11px',
  },
  modelLink: {
    ...FONT_PROXIMA_13,
    color: BLUE,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
});
